import AdUnitVideoFormat from '@/pages/ad-unit/AdUnitVideoFormat';
import DspSelector from '@/pages/ad-unit/DspSelector.tsx';
import {Checkbox, Flex, Form, Input, InputNumber, Radio, Select, Spin} from 'antd';
import {AdUnitBean, DspBean, ItemResponseDspListItemBeanObject} from 'common/api/api';
import {adUnitApi, dspApi} from 'common/api/entitiesApi.tsx';
import {AnchorsForm} from 'common/components/AnchorsForm.tsx';
import {ButtonsPanel} from 'common/components/ButtonsPanel.tsx';
import {AdUnitFormatTypeSelector} from 'common/components/dictionary/AdUnitFormatTypeSelector.tsx';
import {FormCard} from 'common/components/FormCard.tsx';
import {PublisherSelector} from 'common/components/reloadable/PublisherSelector.tsx';
import {SiteSelector} from 'common/components/reloadable/SiteSelector.tsx';
import SizeSelector, {SizeFormatType} from 'common/components/SizeSelector.tsx';
import {useAppContext} from 'common/context/AppContext.tsx';
import {CommonRoutes} from 'common/context/commonRoutes.ts';
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import {apiCall} from 'common/util/Util.tsx';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom'
import DeviceTypesEnum = AdUnitBean.DeviceTypesEnum;
import PositionEnum = AdUnitBean.PositionEnum;
import RevenueTypeEnum = AdUnitBean.RevenueTypeEnum;
import FormatTypeEnum = AdUnitBean.FormatTypeEnum;

const FormItem = Form.Item
const Option = Select.Option
const RadioGroup = Radio.Group
const {TextArea} = Input

export const revenueTypeNames = (type: any) => {
    switch (type) {
        case RevenueTypeEnum.FixedCPM:
            return 'Fixed CPM'
        case RevenueTypeEnum.RevShare:
            return 'Revenue Share'
        default:
            return ''
    }
}

export const positionNames = (type: any) => {
    switch (type) {
        case PositionEnum.Above:
            return 'Above the fold'
        case PositionEnum.Fullscreen:
            return 'Middle of the fold'
        case PositionEnum.Below:
            return 'Below the fold'
        default:
            return ''
    }
}

interface AdUnitFormProps {
    mode: 'create' | 'edit'
}

const isValidSize = (w?: number, h?: number) =>
    typeof w === 'number' && Number.isFinite(w) && w > 0 && typeof h === 'number' && Number.isFinite(h) && h > 0

const AdUnitForm: React.FC<AdUnitFormProps> = ({mode}) => {
    const navigateTo = useNavigate()
    const [form] = Form.useForm()

    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0
    const appContext = useAppContext()
    const [loading, setLoading] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)

    const [dspList, setDspList] = useState<DspBean[]>()
    const [entity, setEntity] = useState<AdUnitBean>({
        revenueType: RevenueTypeEnum.RevShare,
        active: true
    } as AdUnitBean)

    const publisherId = Form.useWatch('publisherId', form)
    const formatType = Form.useWatch('formatType', form)
    const startDelay = Form.useWatch('startDelay', form)
    const revType = Form.useWatch('revenueType', form)

    const width = Form.useWatch('width', form)
    const height = Form.useWatch('height', form)
    const previousFormatType = useRef<FormatTypeEnum | undefined>(undefined)

    const [adSizeFormat, setAdSizeFormat] = useState<SizeFormatType>(SizeFormatType.BANNER)

    const nativeFormat = formatType === FormatTypeEnum.Native
    const sizeRequired = formatType === FormatTypeEnum.Banner || formatType === FormatTypeEnum.Video
    const sizeError = useMemo(
        () => submitAttempted && sizeRequired && !isValidSize(width, height),
        [submitAttempted, sizeRequired, width, height]
    )

    const setDspAdUnitList = (dspAdUnitList: any[]) => {
        setEntity({...entity, dspAdUnitBeans: dspAdUnitList})
    }

    const onCancel = () => navigateTo(CommonRoutes.AD_UNIT)

    const onDelete = () => {
        apiCall(setLoading, appContext, () => adUnitApi.deleteAdUnit(idNum), () => navigateTo(CommonRoutes.AD_UNIT))
    }

    const loadData = () => {
        if (mode === 'edit') {
            apiCall(
                setLoading,
                appContext,
                () => {
                    return adUnitApi.adUnit(idNum)
                },
                (res) => {
                    if (res && res.data) {
                        setEntity(res.data)
                        form.setFieldsValue({...res.data})
                        // form.setFieldsValue({ ...res.data, adSizeBidFloorBeansIds: res.data?.adSizeBidFloorBeans?.map((bean) => bean.adSizeId) })
                    }
                }
            )
        }
        apiCall(
            setLoading,
            appContext,
            () => {
                return dspApi.dspList(
                    undefined, 'asc'
                )
            },
            (res) => {
                const dspList: Array<ItemResponseDspListItemBeanObject> = res.data ?? []
                const itemList = dspList
                    .map((item) => item.data)
                    .filter((item): item is DspBean => item !== undefined)
                setDspList(itemList)
            }
        )
    }

    useEffect(() => {
        loadData()
        form.setFieldsValue({...entity})
    }, [])

    useEffect(() => {
        if (formatType !== FormatTypeEnum.Native) {
            setAdSizeFormat(formatType == FormatTypeEnum.Video ? SizeFormatType.VIDEO : SizeFormatType.BANNER)
        }
        if (formatType === FormatTypeEnum.Native && !isValidSize(form.getFieldValue('width'), form.getFieldValue('height'))) {
            form.setFieldsValue({
                width: 1,
                height: 1,
                size: undefined
            })
        } else if (previousFormatType.current !== undefined && previousFormatType.current !== formatType) {
            form.setFieldsValue({
                width: formatType === FormatTypeEnum.Native ? 1 : undefined,
                height: formatType === FormatTypeEnum.Native ? 1 : undefined,
                size: undefined
            })
        }
        previousFormatType.current = formatType
    }, [formatType]);

    const handleSubmit = () => {
        setSubmitAttempted(true)
        form.validateFields().then((values) => {
            // const newEntity = { ...entity, ...values, adSizeBidFloorBeans: values.adSizeBidFloorBeansIds?.map((id) => ({ adSizeId: id})) }
            const newEntity = {
                ...entity,
                ...values,
                ...(values.formatType === FormatTypeEnum.Native ? {width: 1, height: 1, size: undefined} : {})
            }
            apiCall(
                setLoading,
                appContext,
                () => {
                    if (mode === 'edit' && idNum) {
                        return adUnitApi.editAdUnit(newEntity, idNum)
                    } else {
                        return adUnitApi.createAdUnit(newEntity)
                    }
                },
                (response) => {
                    queueListRepositionOnModifiedChange({
                        appContext,
                        routeKey: CommonRoutes.AD_UNIT,
                        previousEntity: entity,
                        responseEntity: response?.data,
                        enabled: mode === 'edit'
                    })

                    navigateTo(CommonRoutes.AD_UNIT)
                }
            )
        })
    }


    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit} onCancel={onCancel} showAnchors>
                <FormCard id="general" title="General">
                    <FormItem
                        name="publisherId"
                        label="Publisher"
                        rules={[{required: true, message: 'Please select Ad Unit publisher'}]}
                    >
                        <PublisherSelector placeholder="Select Publisher" disabled={mode === 'edit'}/>
                    </FormItem>
                    <FormItem
                        name="siteId"
                        label="Site"
                        required
                        rules={[{required: true, message: 'Please select Ad Unit site'}]}
                    >
                        <SiteSelector
                            placeholder="Select Site"
                            disabled={mode === 'edit' || !publisherId}
                            publisherId={publisherId}
                        />
                    </FormItem>
                    <FormItem name="name" label="Name" required
                              rules={[{required: true, message: 'Please input Ad Unit name'}]}
                    >
                        <Input/>
                    </FormItem>
                    <FormItem name="active" label="Active" valuePropName="checked">
                        <Checkbox/>
                    </FormItem>
                    {mode == 'edit' && <FormItem label="Token" name="token">
                        {entity.token}
                    </FormItem>}
                    <FormItem name="formatType" label="Format" required
                              rules={[{required: true, message: 'Please select Format'}]}
                    >
                        <AdUnitFormatTypeSelector
                            placeholder="Select Format"
                            multiple={false}
                            style={{maxWidth: 200}}
                            showSearch={false}
                            allowClear={false}
                        />
                    </FormItem>

                    <FormItem name="width" hidden noStyle />
                    <FormItem name="height" hidden noStyle />

                    {!nativeFormat && (
                      <FormItem
                        label="Size"
                        name="size"
                        required
                        dependencies={['width', 'height']}
                        validateStatus={sizeError ? 'error' : undefined}
                        rules={[
                          {
                            validator: () => {
                              const w = form.getFieldValue('width')
                              const h = form.getFieldValue('height')
                              return isValidSize(w, h)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Please select Size'))
                            }
                          }
                        ]}
                      >
                        <SizeSelector
                          format={adSizeFormat}
                          width={width}
                          height={height}
                          disabled={formatType == undefined}
                          onChange={(newWidth, newHeight) => {
                            form.setFieldsValue({
                              width: newWidth,
                              height: newHeight,
                              size: newWidth && newHeight ? `${newWidth}x${newHeight}` : undefined
                            })
                            setEntity({ ...entity, width: newWidth as number, height: newHeight as number })
                          }}
                        />
                      </FormItem>
                    )}

                </FormCard>

                <FormCard
                    id="revenue"
                    title="Revenue"
                >
                    <FormItem name="revenueType" label="Revenue Type"
                              required rules={[{required: true, message: 'Please select Revenue Type'}]}
                    >
                        <RadioGroup>
                            {Object.values([RevenueTypeEnum.RevShare, RevenueTypeEnum.FixedCPM]).map((type) => (
                                <Radio key={type} value={type}>
                                    {revenueTypeNames(type)}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </FormItem>
                    {revType === RevenueTypeEnum.FixedCPM && (
                        <FormItem
                            name="fixedCpmPrice"
                            label="Fixed CPM"
                            required
                            rules={[{required: true, message: 'Please input Fixed CPM'}]}
                        >
                            <InputNumber step={0.01} min={0}/>
                        </FormItem>
                    )}
                    {revType === RevenueTypeEnum.RevShare && (
                        <FormItem name="bidFloor" label="Bid Floor" required
                                  rules={[{required: true, message: 'Please input Bid Floor'}]}>
                            <InputNumber step={0.01} min={0}/>
                        </FormItem>
                    )}
                    {revType === RevenueTypeEnum.RevShare && (
                        <FormItem label="Revenue Share" required>
                            <RadioGroup value={entity.revShare == undefined ? 'pub' : 'adunit'}
                                        onChange={e => {
                                            if (e.target.value == 'pub') {
                                                setEntity({...entity, revShare: undefined})
                                            } else {
                                                setEntity({...entity, revShare: entity.publisherRevShare || 50})
                                            }
                                        }}>
                                <Flex align="center" gap={8}>
                                    <Radio key="pub" value="pub">Publisher Default ({entity.publisherRevShare}%)</Radio>
                                    <Flex align="center" gap={0}>
                                        <Radio key="adunit" value="adunit">Custom</Radio>
                                        <InputNumber step={1} min={0} max={99} style={{width: '100px'}}
                                                     addonAfter="%" disabled={entity.revShare == undefined}
                                                     value={entity.revShare}
                                                     onChange={value => setEntity({
                                                         ...entity,
                                                         revShare: value || undefined
                                                     })}
                                        />
                                    </Flex>
                                </Flex>
                            </RadioGroup>
                        </FormItem>
                    )}
                </FormCard>


                <FormCard id="fallback" title="Fallback Tag" required>
                    <FormItem
                      name="defaultAdTag"
                      label="Fallback Tag"
                      required
                      rules={[{required: true, message: 'Please set Fallback code for Ad Unit'}]}
                    >
                        <TextArea rows={4}/>
                    </FormItem>
                </FormCard>

                <FormCard id="dsp" title="DSP Integration">
                    <FormItem name="dspEndpoints" label="Linked DSPs">
                        <DspSelector
                            dspAdUnitList={entity.dspAdUnitBeans || []}
                            setDspAdUnitList={setDspAdUnitList}
                            dspList={dspList || []}
                        />
                    </FormItem>
                    <FormItem name="position" label="Default Position">
                        <Select placeholder="Select position" allowClear>
                            {Object.values(PositionEnum).map((pos) => (
                                <Option key={pos} value={pos}>
                                    {positionNames(pos)}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem name="deviceTypes" label="Default Devices">
                        <Select placeholder="Select devices" mode="multiple" allowClear>
                            {Object.values(DeviceTypesEnum).map((deviceType) => (
                                <Option key={deviceType} value={deviceType}>
                                    {deviceType}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                    {FormatTypeEnum.Video === formatType && (
                        <AdUnitVideoFormat
                            startDelay={startDelay}
                            customMidRoll={0}
                            placement={entity.placement}
                            linearity={entity.linearity}
                        />
                    )}
                </FormCard>
                <ButtonsPanel onCancel={onCancel} showDelete={mode === 'edit'} onDelete={onDelete}/>
            </AnchorsForm>
        </Spin>
    )
}

export default AdUnitForm
